import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { ConfirmationService } from "primeng/api";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { GoogleAnalyticsService } from "src/app/core/services/google-analytics.service";
import { LocalDataStoreService } from "src/app/core/services/local-data-store.service";
import { OrderService } from "src/app/core/services/order.service";
import { PaymentService } from "src/app/core/services/payment.service";
import { CheckDto, CheckItemDto, CheckPaymentDto, CheckSeatDto } from "src/app/shared/models/check-dto";
import { EGiftPromoCode } from "src/app/shared/models/eGift/EGiftPromoCode.model";
import { EGiftTransaction } from "src/app/shared/models/eGift/EGiftTransaction.model";
import { EGiftVenue } from "src/app/shared/models/eGift/EGiftVenue.model";
import { CCProcessors } from "src/app/shared/models/enums/CCProcessors.enum";
import { FocusLinkProduct } from "src/app/shared/models/enums/FocusLInkProduct.enum";
import { ProductUseFunction } from "src/app/shared/models/enums/ProductUseFunction.enum";
import { OOAliasUrl } from "src/app/shared/models/OOAliasUrl.model";

@Component({
	selector: "app-e-gift",
	templateUrl: "./e-gift.component.html",
	styleUrls: ["../gift.style.scss"],
})
export class EGiftComponent implements OnInit {
	isMobile: boolean = false;

	eGiftVenue: EGiftVenue;

	moment: any = moment;
	prepurchase: boolean = true;
	promoCode: EGiftPromoCode;

	transaction: EGiftTransaction;
	transactionAmount: number;
	transactionComplete: boolean;
	value: number;
	venueKey: number;

	constructor(
		public app: AppService,
		public breakpointObserver: BreakpointObserver,
		private confirmationService: ConfirmationService,
		private googleAnalytics: GoogleAnalyticsService,
		private focuslink: FocuslinkService,
		private route: ActivatedRoute,
		private storage: LocalDataStoreService,
		private titleService: Title,
		public orderService: OrderService,
		public paymentService: PaymentService
	) {}

	ngOnInit(): void {
		this.breakpointObserver.observe(["(max-width: 450px)"]).subscribe((state: BreakpointState) => {
			if (state.matches) {
				this.isMobile = true;
			} else {
				this.isMobile = false;
			}
		});

		const venueIdentifier = this.route.snapshot.paramMap.get("venueKey");
		if (isNaN(+venueIdentifier)) {
			this.initFromAlias(venueIdentifier);
		} else {
			this.venueKey = +venueIdentifier;
			this.init(this.venueKey);
		}

		this.venueKey = +this.route.snapshot.paramMap.get("venueKey");
		if (this.venueKey) {
			this.init(this.venueKey);
		}
	}

	async initFromAlias(alias: string) {
		try {
			const resp = await this.focuslink.aliasGet(alias);
			const ooAlias = new OOAliasUrl(resp);
			if (ooAlias.venueKey) {
				this.venueKey = ooAlias.venueKey;
				this.init(ooAlias.venueKey);
			}
		} catch (error) {}
	}

	async init(venueKey: number) {
		this.app.initProduct(this.venueKey, FocusLinkProduct.EGift, ProductUseFunction.STARTED);
		await this.getVenue(venueKey);

		this.orderService.activeCheckKey.next(this.app.uuidv4());
		this.googleAnalytics.init("G-RT9EJ29S29", "eGift");

		this.titleService.setTitle(`Gift Cards from ${this.eGiftVenue.Venue.ooVenue.name}`);
	}

	async getVenue(venueKey) {
		let resp = await this.focuslink.eGiftVenueGet(venueKey);

		this.eGiftVenue = new EGiftVenue(resp);
		if (!this.eGiftVenue.logoS3Key) {
			this.eGiftVenue.logoS3Key = "logo.png";
		}
		await this.orderService.venueService.ooVenueGet(venueKey);
		this.googleAnalytics.setVenue(venueKey, this.orderService.venueService.ooVenue?.name);
		if (this.orderService.venueService.ooVenue.ccProcessor === CCProcessors.FOCUSPAYADYEN) {
			this.paymentService.initPaymentService();
		}
	}

	async getPromoCode(venueKey: number, promoCode: string) {
		try {
			const resp = await this.focuslink.eGiftPromoCodeGet(venueKey, promoCode);
			this.promoCode = new EGiftPromoCode(resp);
			return this.promoCode;
		} catch (error) {
			return undefined;
		}
	}

	async promoCodeMiniumPrompt(transaction: EGiftTransaction, promoCode: EGiftPromoCode): Promise<boolean> {
		this.confirmationService.confirm({
			message: `Your gift card purchase does not meet the minimum for the ${promoCode.promoCode} code of $${promoCode.minimumValue}. Continue?`,
			accept: () => {
				return true;
			},
			reject: () => {
				return false;
			},
		});
		return false;
	}

	async checkout() {
		this.transaction.value = +this.value;
		this.transaction.transactionAmount = this.transactionAmount ? this.transactionAmount : this.transaction.value;
		this.transaction.promoCode = this.promoCode ? this.promoCode.promoCode : undefined;
		if (this.transaction.promoCode) {
			if (this.promoCode) {
				if (this.promoCode.minimumValue <= this.transaction.value) {
					this.transaction.transactionAmount = +((Math.round(this.transaction.value - this.promoCode.value) * 100) / 100).toFixed(2);
				}
			}
		}

		if (!this.transaction.recipientName) {
			this.transaction.recipientName = `${this.transaction.purchaserFirstName} ${this.transaction.purchaserLastName}`;
			this.transaction.recipientEmail = this.transaction.purchaserEmail;
		}
		this.transaction.invoice = moment().unix().toString();
		this.prepurchase = false;
	}

	async completePurchase(transaction: EGiftTransaction) {
		this.app.loading(true);
		transaction.orderPayment.paymentKey = this.paymentService.paymentKey.value || undefined;
		try {
			const ooMenuConfig = await this.focuslink.getOOMenuConfig(this.eGiftVenue.venueKey);
			this.transaction = transaction;
			this.transaction.check = new CheckDto({
				venueKey: this.eGiftVenue.venueKey,
				source: "focuspos-egift",
				checkKey: this.orderService.activeCheckKey.value,
				table: this.transaction.purchaserLastName,
				numberOfGuests: 1,
				employeeId: ooMenuConfig.employeeRecNum,
				seats: [
					new CheckSeatDto({
						seatKey: 1,
						payments: [CheckPaymentDto.fromOrderPayment(this.transaction.orderPayment, "focuspos-egift")],
						items: [
							new CheckItemDto({
								menuItemKey: this.eGiftVenue.menuItemKey,
								name: `Gift Card Sale`,
								quantity: 1,
								price: this.transaction.value,
								modifiers: [],
							}),
						],
					}),
				],
			});

			if (this.transaction.promoCode) {
				this.transaction.check.seats[0].items[0].modifiers.push(
					new CheckItemDto({
						menuItemKey: this.promoCode.menuItemKey,
						price: -this.promoCode.value,
						quantity: 1,
					})
				);
			}

			const resp = await this.focuslink.eGiftTransactionPost(this.eGiftVenue.venueKey, this.transaction);
			this.transactionComplete = true;

			if (this.orderService.venueService.ooVenue.ccProcessor === CCProcessors.SHIFT4) {
				await this.focuslink.customerInvoicePost(
					this.orderService.venueService.ooVenue.venueKey,
					this.transaction.check.checkKey,
					+transaction.invoice,
					"egift",
					transaction.token
				);
			}
			this.app.initProduct(this.venueKey, FocusLinkProduct.EGift, ProductUseFunction.TRANSACTIONCOMPLETE, this.transaction.check.checkKey);
			this.app.loading(false);
		} catch (error) {
			this.app.loading(false);
			this.app.msgSvc.add({ severity: "error", detail: error.error });
		}
	}

	async reset() {
		this.prepurchase = true;
		this.transactionComplete = false;
		this.transaction = undefined;
		this.value = undefined;
		this.promoCode = undefined;
		this.eGiftVenue = undefined;
		this.init(this.venueKey);
	}
}
